import { from as observableFrom, Subject, Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { MediaMapper } from "./repositories/media/media.mapper";
import { MediaInfo } from "../domain/media";
import { Logger } from "./logger";
import { UserService } from "../domain/user-service";
import { Auth } from "./auth";
import { environment } from "app/environments/environment";
import { ChannelPreview } from "../domain/channel-preview";
import { Channel } from "../domain/channel";
import { WebRTCPreview } from "../domain/web-r-t-c-preview";
import {
  LiveFeedPreview,
  AvailabilityzoneUrl,
} from "../domain/live-feed-preview";
import { ChannelFuturePreview } from "../domain/channel-future-preview";
import moment from "moment";
import * as signalR from "@microsoft/signalr";
import { ToastrService } from "ngx-toastr";

const TAG = "Socket:";

export interface EnteredBroadcastEditPageResult {
  alreadyInUse: boolean;
  currentUsername?: string | null;
}

export interface EnteredBroadcastLivePageResult {
  alreadyInUse: boolean;
  currentUsername?: string | null;
}

/**
 * Socket Class
 */
@Injectable()
export class Socket {
  hubConnection: signalR.HubConnection;
  reportsHubConnection: signalR.HubConnection;
  recordingsHubConnection: signalR.HubConnection;
  liveBroadcastingHubConnection: signalR.HubConnection;
  /** EPG Schedule hub — wireframe for sparse patch updates (event_updated, live_flag_change, etc.). Backend hub not yet implemented. */
  epgHubConnection: signalR.HubConnection | null = null;

  connectionId: string;

  uploadFinishedChange = new Subject<MediaInfo>();
  packshotUploadFinishedChange = new Subject<{
    mediaId: number;
    url: string;
    uploadSuccess: boolean;
  }>();
  mediaQualityChanged = new Subject<{ mediaId: number; quality: number }>();
  private videoLogoAnalyzeFinished = new Subject<{
    uploadSuccess: boolean;
    url: string;
  }>();
  private retryInMs = 1000;
  private connected = false;
  videoLogoAnalyzeFinishedObservable: Observable<{
    uploadSuccess: boolean;
    url: string;
  }>;

  onHubDisconnected = new Subject<void>();
  onHubReconnected = new Subject<void>();

  private selectedTransport: string | null = null;

  constructor(
    private mediaMapper: MediaMapper,
    private logger: Logger,
    private auth: Auth,
    private userService: UserService,
    private _toastr: ToastrService,
  ) {
    this.videoLogoAnalyzeFinishedObservable = observableFrom(
      this.videoLogoAnalyzeFinished,
    );
  }

  init() {
    return new Promise((resolve, reject) => {
      try {
        // Force close any existing connections first
        if (this.hubConnection) {
          this.hubConnection.stop();
        }
        if (this.reportsHubConnection) {
          this.reportsHubConnection.stop();
        }
        if (this.recordingsHubConnection) {
          this.recordingsHubConnection.stop();
        }
        if (this.liveBroadcastingHubConnection) {
          this.liveBroadcastingHubConnection.stop();
        }
        if (this.epgHubConnection) {
          this.epgHubConnection.stop();
          this.epgHubConnection = null;
        }

        // Create main hub connection
        this.hubConnection = this.hubConnectionBuilder(
          environment.ENDPOINT + "/signalr/ichannelHub",
        );
        if (!this.hubConnection) {
          console.error("Failed to create hub connection - no token available");
          return reject(new Error("No authentication token available"));
        }

        // Initialize other hubs
        this.initReportsHub();
        this.initRecordingsHub();
        this.initLiveBroadcastingHub();
        this.initEpgHub();

        // Set up event handlers
        this.hubConnection.on(
          "uploadFinished",
          this.onUploadFinished.bind(this),
        );
        this.hubConnection.on(
          "mediaPackshotUploadFinished",
          this.onMediaPackshotUploadFinished.bind(this),
        );
        this.hubConnection.on(
          "mediaQualityChanged",
          this.onMediaQualityChanged.bind(this),
        );
        this.hubConnection.on(
          "VideoLogoAnalyzeFinished",
          this.onVideoLogoAnalyzeFinished.bind(this),
        );

        // Add connection monitoring
        this.hubConnection.onreconnecting((error) => {
          if (
            this.selectedTransport &&
            this.selectedTransport.toLowerCase() === "websockets"
          ) {
            this._toastr.warning(
              `Connection lost. Trying to reconnect...`,
              "Network",
              {
                disableTimeOut: true,
                closeButton: true,
                tapToDismiss: false,
              },
            );
          }
          console.log(
            TAG,
            `Connection lost due to error "${error}". Reconnecting...`,
          );
          this.connected = false;
        });

        this.hubConnection.onreconnected((connectionId) => {
          console.log(
            TAG,
            `Connection reestablished. Connected with ID "${connectionId}".`,
          );
          if (
            this.selectedTransport &&
            this.selectedTransport.toLowerCase() === "websockets"
          ) {
            this._toastr.clear();
            this._toastr.success(`Connection re-established.`, "Network");
          }
          this.connectionId = connectionId;
          this.connected = true;
          this.onHubReconnected.next();
        });

        // Set up reconnection event handler
        this.hubConnection.onclose((error) => {
          console.log(
            TAG,
            `[${this.connectionId}] got disconnected. Automatic reconnection will be attempted. error: ${error}`,
          );
          this.connected = false;
          this.onHubDisconnected.next();
        });

        this.hubConnection.on("sayHello", (connectionId) => {
          this.connectionId = connectionId;
          console.log(
            TAG,
            `Connected to IChannelHub, ConnectionId: ${connectionId}`,
          );
          this.connected = true;
        });

        // Start the connection
        this.hubConnection
          .start()
          .then(() => {
            console.log(TAG, `SignalR connection started successfully`);
            this.connected = true;
            resolve(true);
          })
          .catch((err) => {
            console.error(TAG, "Error starting SignalR connection:", err);
            this.connected = false;
            if (environment.production) {
              this.auth.remove();
              window.location.href = "/login";
            }
            reject(err);
          });
      } catch (error) {
        console.error(TAG, "Error in init:", error);
        if (environment.production) {
          this.auth.remove();
          window.location.href = "/login";
        }
        reject(error);
      }
    });
  }

  connect() {
    return new Promise<boolean>((resolve, reject) => {
      if (!this.hubConnection) {
        console.error(TAG, "No hub connection available");
        return reject(new Error("No hub connection available"));
      }

      if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
        console.log(TAG, "Already connected to SignalR hub");
        return resolve(true);
      }

      this.hubConnection
        .start()
        .then(() => {
          console.log(TAG, `SignalR connection started successfully`);
          this.connected = true;
          resolve(true);
        })
        .catch((err) => {
          console.error(TAG, "Error starting SignalR connection:", err);
          this.connected = false;
          reject(err);
        });
    });
  }

  private initReportsHub() {
    this.reportsHubConnection = this.hubConnectionBuilder(
      environment.ENDPOINT + "/signalr/reportsHub",
    );

    this.reportsHubConnection.on("sayHello", (msg) => {
      console.log(TAG, msg);
    });

    this.reportsHubConnection
      .start()
      .then(() => console.log(TAG, `ReportsHub connected`))
      .catch((err) =>
        console.error("Error starting reportsHub connection:", err),
      );
  }

  private initRecordingsHub() {
    this.recordingsHubConnection = this.hubConnectionBuilder(
      environment.ENDPOINT + "/signalr/recording",
    );
    this.recordingsHubConnection.on("sayHello", (msg) => {
      console.log(TAG, msg);
    });

    this.recordingsHubConnection
      .start()
      .then(() => console.log(TAG, `RecordingsHub connected`))
      .catch((err) =>
        console.error("Error starting recordingsHub connection:", err),
      );
  }

  private initLiveBroadcastingHub() {
    this.liveBroadcastingHubConnection = this.hubConnectionBuilder(
      environment.ENDPOINT + "/signalr/liveBroadcastHub",
    );

    this.liveBroadcastingHubConnection.on("sayHello", (msg) => {
      console.log(
        TAG,
        `Connected to LiveBroadcastingHub, ConnectionId: ${msg}`,
      );
    });

    this.liveBroadcastingHubConnection
      .start()
      .then(() => console.log(TAG, `LiveBroadcastingHub connected`))
      .catch((err) =>
        console.error("Error starting liveBroadcastingHub connection:", err),
      );
  }

  /**
   * EPG Schedule hub wireframe.
   * Expected server events: event_created, event_updated, event_deleted, recording_status, live_flag_change, clip_count_update.
   * Backend hub (e.g. /signalr/epgHub) not yet implemented — connection is optional and failures are non-fatal.
   */
  private initEpgHub() {
    this.epgHubConnection = this.hubConnectionBuilder(
      environment.ENDPOINT + "/signalr/epgHub",
    );
    if (!this.epgHubConnection) {
      return;
    }
    this.epgHubConnection.on("sayHello", (msg) => {
      console.log(TAG, `Connected to EpgHub, ConnectionId: ${msg}`);
    });
    this.epgHubConnection
      .start()
      .then(() => console.log(TAG, "EpgHub connected"))
      .catch((err) => {
        console.warn(TAG, "EpgHub not available (backend may not implement it yet):", err?.message ?? err);
        this.epgHubConnection = null;
      });
  }

  /** Register handler for EPG sparse-patch messages. Call after EpgHub is connected. */
  onEpgMessage(
    handler: (message: {
      type: string;
      event_id?: string;
      channel_id?: string;
      changed_fields?: Record<string, unknown>;
      timestamp: string;
      source_id?: string;
    }) => void,
  ): void {
    if (!this.epgHubConnection) return;
    const events = [
      "event_created",
      "event_updated",
      "event_deleted",
      "recording_status",
      "live_flag_change",
      "clip_count_update",
    ];
    events.forEach((eventName) => {
      this.epgHubConnection.on(eventName, (payload: any) => {
        handler({ type: eventName, ...payload });
      });
    });
  }

  /** Remove EPG message handler (e.g. on component destroy). */
  offEpgMessage(): void {
    if (!this.epgHubConnection) return;
    [
      "event_created",
      "event_updated",
      "event_deleted",
      "recording_status",
      "live_flag_change",
      "clip_count_update",
    ].forEach((eventName) => this.epgHubConnection.off(eventName));
  }

  private hubConnectionBuilder(url: string) {
    // Get the current token
    const currentToken = this.auth.token?.token;
    if (!currentToken) {
      console.error("No authentication token available");
      return null;
    }

    // Add cache-busting parameter
    const cacheBuster = `?t=${Date.now()}`;
    const urlWithCacheBuster = url + cacheBuster;
    var options: signalR.IHttpConnectionOptions = {
      accessTokenFactory: () => currentToken,
    };
    if (!environment.production) {
      options.skipNegotiation = true;
      options.transport = signalR.HttpTransportType.WebSockets;
      // Negotiation is skipped, so SignalR never logs "Selecting
      // transport" and customLogger can't capture it — set it directly.
      this.selectedTransport = "websockets";
    }
    let connection = new signalR.HubConnectionBuilder()
      .withUrl(urlWithCacheBuster, options)
      .configureLogging({ log: this.logSignalR.bind(this) })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();
    connection.serverTimeoutInMilliseconds = 60000;
    return connection;
  }

  onUploadFinished(content) {
    this.logger.debug("socket.onUploadFinished => start");
    if (!content || !content.message) {
      this.logger.error("socket.onUploadFinished=> message was empty");
      return;
    }
    if (
      this.userService.getCurrentUser().userId ===
      content.message.UploaderUserId
    ) {
      this.mediaMapper
        .mapGetMediaInfoResponse(content.message)
        .then((mediaInfo) => {
          this.uploadFinishedChange.next(mediaInfo);
        })
        .catch((err) => {
          this.logger.error(
            "socket.onUploadFinished=> error parsing content: " + content,
          );
        });
    }
  }
  private logSignalR(logLevel: signalR.LogLevel, message: string) {
    // Look for the transport selection message
    const transportMatch = message.match(/Selecting transport '(\w+)'/);
    if (transportMatch) {
      this.selectedTransport = transportMatch[1];
    }

    if (environment.production && logLevel != signalR.LogLevel.Critical) {
      return;
    }
    console.log(`[${signalR.LogLevel[logLevel]}]: ${message}`);
  }

  onMediaPackshotUploadFinished(content) {
    this.logger.debug("socket.onMediaPackshotUploadFinished => start");
    if (!content || !content.message) {
      this.logger.error(
        "socket.onMediaPackshotUploadFinished=> message was empty",
      );
      return;
    }
    this.packshotUploadFinishedChange.next({
      uploadSuccess: content.message.uploadSuccess ?? content.message.UploadSuccess,
      url: content.message.url ?? content.message.Url,
      mediaId: content.message.mediaId ?? content.message.MediaId,
    });
  }

  createChannelPreview() {
    return this.userService.getCurrentChannel().then((channel) => {
      return new Promise((resolve, reject) => {
        this.hubConnection
          .invoke("createChannelPreview", {
            ChannelId: channel.id,
            OffsetMs: 0,
          })
          .then(
            (result: {
              Id: string;
              Name: string;
              PlaceHolder: string;
              Url: string;
            }) => {
              resolve({
                id: result.Id,
                name: result.Name,
                placeholder: result.PlaceHolder,
                url: result.Url,
              });
            },
          )
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      });
    });
  }

  enteredLivePageEventHandler(
    channelId: number,
    username: string,
    livePageClientId: string,
    onUserEnteredLivePage: (
      channelId: number,
      username: string,
      time: Date,
    ) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.hubConnection
        .invoke("EnteredLivePageEventHandler", {
          ChannelId: channelId,
          Username: username,
          ClientId: livePageClientId,
        })
        .then(() => {
          if (onUserEnteredLivePage) {
            this.hubConnection.on("userEnteredLivePage", onUserEnteredLivePage);
          }
          resolve();
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  stopListeningForOtherUserEnteredLivePage(
    onUserEnteredLivePage: (
      channelId: number,
      username: string,
      time: Date,
    ) => void,
  ) {
    this.hubConnection.off("userEnteredLivePage", onUserEnteredLivePage);
  }

  enteredBroadcastEditPageEventHandler(
    broadcastPresetUid: string,
    username: string,
    clientId: string,
    onUserEnteredBroadcastEditPage: (
      broadcastPresetUid: string,
      username: string,
      time: Date,
    ) => void,
  ): Promise<EnteredBroadcastEditPageResult> {
    return new Promise<EnteredBroadcastEditPageResult>((resolve, reject) => {
      this.hubConnection
        .invoke("EnteredBroadcastEditPage", {
          BroadcastPresetUid: broadcastPresetUid,
          Username: username,
          ClientId: clientId,
        })
        .then((result: EnteredBroadcastEditPageResult) => {
          if (!result.alreadyInUse && onUserEnteredBroadcastEditPage) {
            this.hubConnection.on(
              "userEnteredBroadcastEditPage",
              onUserEnteredBroadcastEditPage,
            );
          }
          resolve(result);
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  takeOverBroadcastEditPage(
    broadcastPresetUid: string,
    username: string,
    clientId: string,
  ): Promise<void> {
    return this.hubConnection.invoke("TakeOverBroadcastEditPage", {
      BroadcastPresetUid: broadcastPresetUid,
      Username: username,
      ClientId: clientId,
    });
  }

  registerBroadcastEditPageListener(
    onUserEnteredBroadcastEditPage: (
      broadcastPresetUid: string,
      username: string,
      time: Date,
    ) => void,
  ): void {
    this.hubConnection.on(
      "userEnteredBroadcastEditPage",
      onUserEnteredBroadcastEditPage,
    );
  }

  enteredBroadcastLivePageEventHandler(
    broadcastPresetUid: string,
    username: string,
    clientId: string,
    onUserEnteredBroadcastLivePage: (
      broadcastPresetUid: string,
      username: string,
      time: Date,
    ) => void,
  ): Promise<EnteredBroadcastLivePageResult> {
    return new Promise<EnteredBroadcastLivePageResult>((resolve, reject) => {
      this.hubConnection
        .invoke("EnteredBroadcastLivePage", {
          BroadcastPresetUid: broadcastPresetUid,
          Username: username,
          ClientId: clientId,
        })
        .then((result: EnteredBroadcastLivePageResult) => {
          if (!result.alreadyInUse && onUserEnteredBroadcastLivePage) {
            this.hubConnection.on(
              "userEnteredBroadcastLivePage",
              onUserEnteredBroadcastLivePage,
            );
          }
          resolve(result);
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  takeOverBroadcastLivePage(
    broadcastPresetUid: string,
    username: string,
    clientId: string,
  ): Promise<void> {
    return this.hubConnection.invoke("TakeOverBroadcastLivePage", {
      BroadcastPresetUid: broadcastPresetUid,
      Username: username,
      ClientId: clientId,
    });
  }

  registerBroadcastLivePageListener(
    onUserEnteredBroadcastLivePage: (
      broadcastPresetUid: string,
      username: string,
      time: Date,
    ) => void,
  ): void {
    this.hubConnection.on(
      "userEnteredBroadcastLivePage",
      onUserEnteredBroadcastLivePage,
    );
  }

  stopListeningForBroadcastEditPage(
    onUserEnteredBroadcastEditPage: (
      broadcastPresetUid: string,
      username: string,
      time: Date,
    ) => void,
  ) {
    this.hubConnection.off(
      "userEnteredBroadcastEditPage",
      onUserEnteredBroadcastEditPage,
    );
  }

  stopListeningForBroadcastLivePage(
    onUserEnteredBroadcastLivePage: (
      broadcastPresetUid: string,
      username: string,
      time: Date,
    ) => void,
  ) {
    this.hubConnection.off(
      "userEnteredBroadcastLivePage",
      onUserEnteredBroadcastLivePage,
    );
  }

  leaveBroadcastEditPage(broadcastPresetUid: string): Promise<void> {
    return this.hubConnection.invoke("LeaveBroadcastEditPage", {
      BroadcastPresetUid: broadcastPresetUid,
    });
  }

  leaveBroadcastLivePage(broadcastPresetUid: string): Promise<void> {
    return this.hubConnection.invoke("LeaveBroadcastLivePage", {
      BroadcastPresetUid: broadcastPresetUid,
    });
  }

  startListeningForSchedulerUpdates(
    channelId: number,
    onSchedulerUpdatedEventHandler: (
      channelId: number,
      startTimeUtc: Date,
      endTimeUtc: Date,
    ) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.hubConnection
        .invoke("StartListeningForSchedulerUpdates", {
          ChannelId: channelId,
        })
        .then(() => {
          if (onSchedulerUpdatedEventHandler) {
            this.hubConnection.on(
              "schedulerUpdated",
              onSchedulerUpdatedEventHandler,
            );
          }
          resolve();
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  stopListeningForSchedulerUpdates(
    onSchedulerUpdatedEventHandler: (
      channelId: number,
      startTimeUtc: Date,
      endTimeUtc: Date,
    ) => void,
  ) {
    this.hubConnection.off("schedulerUpdated", onSchedulerUpdatedEventHandler);
  }

  startListeningForLiveBroadcastStackAssetItemUpdates(
    channelUid: string,
    broadcastPresetUid: string,
    onLiveBroadcastStackAssetItemUpdatedEventHandler: (
      channelUid: string,
      broadcastPresetUid: string,
      stackAssetItem: string,
      stackEnded: boolean,
    ) => void,
  ): Promise<void> {
    const normalizedChannelUid =
      typeof channelUid === "string" ? channelUid.toLowerCase() : channelUid;
    const normalizedBroadcastPresetUid =
      typeof broadcastPresetUid === "string"
        ? broadcastPresetUid.toLowerCase()
        : broadcastPresetUid;
    return new Promise<void>((resolve, reject) => {
      this.hubConnection
        .invoke("startListeningForLiveBroadcastStackAssetItemUpdates", {
          ChannelUid: normalizedChannelUid,
          BroadcastPresetUid: normalizedBroadcastPresetUid,
        })
        .then(() => {
          this.hubConnection.off("broadcastPresetStackAssetItemIndexChanged");
          if (onLiveBroadcastStackAssetItemUpdatedEventHandler) {
            this.hubConnection.on(
              "broadcastPresetStackAssetItemIndexChanged",
              onLiveBroadcastStackAssetItemUpdatedEventHandler.bind(this),
            );
          }
          resolve();
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  stopListeningForLiveBroadcastStackAssetItemUpdates(): void {
    this.hubConnection.off("broadcastPresetStackAssetItemIndexChanged");
  }

  stopListeningForLiveBroadcastStackUpdates() {
    this.hubConnection.off("liveBroadcastStackUpdated");
  }

  startListeningForLiveBroadcastingUpdates(
    channelId: number,
    onLiveBroadcastingUpdatedEventHandler: (channelId: number) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.hubConnection
        .invoke("StartListeningForLiveBroadcastingUpdates", {
          ChannelId: channelId,
        })
        .then(() => {
          if (onLiveBroadcastingUpdatedEventHandler) {
            this.hubConnection.on(
              "liveBroadcastUpdated",
              onLiveBroadcastingUpdatedEventHandler.bind(this),
            );
          }
          resolve();
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  startListeningForChannelBroadcastUpdates(
    channelUid: string,
    broadcastPresetUid: string,
    onChannelBroadcastUpdatesEventHandler: (
      channelUid: string,
      broadcastPresetUid: number,
      assetItemsUids: string[],
    ) => void,
  ): Promise<void> {
    const normalizedChannelUid =
      typeof channelUid === "string" ? channelUid.toLowerCase() : channelUid;
    const normalizedBroadcastPresetUid =
      typeof broadcastPresetUid === "string"
        ? broadcastPresetUid.toLowerCase()
        : broadcastPresetUid;
    return new Promise<void>((resolve, reject) => {
      this.hubConnection
        .invoke("StartListeningForChannelBroadcastUpdates", {
          BroadcastPresetUid: normalizedBroadcastPresetUid,
          ChannelUid: normalizedChannelUid,
        })
        .then(() => {
          if (onChannelBroadcastUpdatesEventHandler) {
            this.hubConnection.on(
              "broadcastPresetUpdatedOnChannel",
              onChannelBroadcastUpdatesEventHandler,
            );
          }
          resolve();
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  stopListeningForChannelBroadcastUpdates() {
    this.hubConnection.off("broadcastPresetUpdatedOnChannel");
  }

  stopListeningForLiveBroadcastingUpdates() {
    this.hubConnection.off("liveBroadcastUpdated");
  }

  startListeningForChannelStatusUpdates(
    channelId: number,
    onChannelStatusUpdatedEventHandler: (channelId: number) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.hubConnection
        .invoke("StartListeningForChannelStatusUpdates", {
          ChannelId: channelId,
        })
        .then(() => {
          if (onChannelStatusUpdatedEventHandler) {
            this.hubConnection.on(
              "channelStatusUpdated",
              onChannelStatusUpdatedEventHandler.bind(this),
            );
          }
          resolve();
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  startListeningForChannelAutopilotUpdates(
    channelId: number,
    onChannelAutopilotUpdatedEventHandler: (
      channelId: number,
      autopilotRuleId: string,
    ) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.hubConnection
        .invoke("StartListeningForChannelAutopilotUpdates", {
          ChannelId: channelId,
        })
        .then(() => {
          if (onChannelAutopilotUpdatedEventHandler) {
            this.hubConnection.on(
              "channelAutopilotUpdated",
              onChannelAutopilotUpdatedEventHandler.bind(this),
            );
          }
          resolve();
        })
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }


  stopListeningForChannelAutopilotUpdates() {
    this.hubConnection.off("channelAutopilotUpdated");
  }

  stopListeningForChannelStatusUpdates() {
    this.hubConnection.off("channelStatusUpdated");
  }

  createChannelWebRTCPreview(channel: Channel): Promise<WebRTCPreview> {
    return new Promise<WebRTCPreview>((resolve, reject) => {
      this.hubConnection
        .invoke("CreateChannelWebRTCPreview", {
          ChannelId: channel.id,
        })
        .then(
          (result: {
            clientId: string;
            brodcasterId: string;
            availabilityZoneUrls: Array<string>;
            tcpOnlyWebRTC?: boolean;
            tCPOnlyWebRTC?: boolean;
            TCPOnlyWebRTC?: boolean;
          }) => {
            const tcpOnly =
              result.tcpOnlyWebRTC ??
              result.tCPOnlyWebRTC ??
              result.TCPOnlyWebRTC ??
              false;
            resolve(
              new WebRTCPreview(
                result.clientId,
                result.brodcasterId,
                result.availabilityZoneUrls,
                tcpOnly,
              ),
            );
          },
        )
        .catch((err: any) => {
          console.error(err);
          reject(err);
        });
    });
  }

  deactivateChannelPreview(id: string) {
    return new Promise((resolve, reject) => {
      this.hubConnection
        .invoke("deactivateChannelPreview", id)
        .then(() => resolve(true))
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateChannelPreviewOffset(previewId: string, offsetMs: number) {
    return new Promise((resolve, reject) => {
      this.hubConnection
        .invoke("updateChannelPreviewOffset", {
          PreviewId: previewId,
          OffsetMs: offsetMs,
        })
        .then(() => resolve(true))
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateChannelScheduleSpecificTime(nextShowId: string) {
    return new Promise((resolve, reject) => {
      this.hubConnection
        .invoke("UpdateChannelScheduleSpecificTime", { NextShowId: nextShowId })
        .then(() => resolve(true))
        .catch((err) => {
          reject(err);
        });
    });
  }

  async deletePreviewChannel(previewChannelId: string): Promise<void> {
    console.log("deletePreviewChannel ", previewChannelId);
    await this.hubConnection.invoke("DeletePreviewChannel", previewChannelId);
  }

  async updatePreviewChannel(
    previewChannelId: string,
    newStartTimeUtc: moment.Moment,
  ): Promise<void> {
    console.log("updatePreviewChannel ", previewChannelId, newStartTimeUtc);
    await this.hubConnection.invoke(
      "UpdateFuturePreviewChannel",
      previewChannelId,
      newStartTimeUtc.format("YYYYMMDD"),
      newStartTimeUtc.format("HHmmss"),
    );
  }

  async createChannelFuturePreview(
    channelId: number,
    startTimeUtc: moment.Moment,
    previewDurationMs: number,
  ): Promise<ChannelFuturePreview> {
    console.log(
      "createChannelFuturePreview",
      startTimeUtc.format("YYYYMMDD"),
      startTimeUtc.format("HHmmss"),
    );
    const result = await this.hubConnection.invoke(
      "CreateChannelFuturePreview",
      {
        ChannelId: channelId,
        StartDateUtc: startTimeUtc.format("YYYYMMDD"),
        StartTimeUtc: startTimeUtc.format("HHmmss"),
        DurationMs: previewDurationMs,
      },
    );
    const availabilityZoneUrls: AvailabilityzoneUrl = new AvailabilityzoneUrl();

    for (const azName in result.availabilityZoneUrls) {
      if (result.availabilityZoneUrls.hasOwnProperty(azName)) {
        const az = result.availabilityZoneUrls[azName];
        availabilityZoneUrls[azName] = {
          id: az.id,
          displayName: az.displayName,
          url: az.url,
        };
      }
    }
    const tcpOnly =
      result.tcpOnlyWebRTC ??
      result.tCPOnlyWebRTC ??
      result.TCPOnlyWebRTC ??
      false;
    const channelFuturePreview = new ChannelFuturePreview(
      result.id,
      result.clientId,
      result.brodcasterId,
      availabilityZoneUrls as any,
      tcpOnly,
    );
    return channelFuturePreview;
  }

  createLiveFeedPreview(
    feedId: string,
    availabilityZoneId?: number,
    overrideStreamingPlatformId?: number,
  ): Promise<LiveFeedPreview> {
    return new Promise((resolve, reject) => {
      this.hubConnection
        .invoke("createLiveFeedPreview", {
          FeedId: feedId,
          AvailabilityZoneId: availabilityZoneId,
          OverrideStreamingPlatformId: overrideStreamingPlatformId,
        })
        .then((result: any) => {
          const availabilityZoneUrls: AvailabilityzoneUrl =
            new AvailabilityzoneUrl();

          for (const azName in result.availabilityZoneUrls) {
            if (result.availabilityZoneUrls.hasOwnProperty(azName)) {
              const az = result.availabilityZoneUrls[azName];
              availabilityZoneUrls[azName] = {
                id: az.id,
                displayName: az.displayName,
                url: az.url,
              };
            }
          }
          const liveFeedPreview = new LiveFeedPreview(
            result.id,
            result.clientId,
            result.brodcasterId,
            availabilityZoneUrls as any,
          );
          resolve(liveFeedPreview);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  deactivateLiveFeedPreview(previewId: string) {
    return new Promise((resolve, reject) => {
      this.hubConnection
        .invoke("deactivateLiveFeedPreview", previewId)
        .then(() => {
          resolve(true);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  updateLiveFeedPreviewFeed(previewId: string, feedId: string) {
    return new Promise((resolve, reject) => {
      this.hubConnection
        .invoke("updateLiveFeedPreviewFeed", {
          PreviewId: previewId,
          FeedId: feedId,
        })
        .then(() => {
          resolve(true);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  onMediaQualityChanged(result) {
    this.mediaQualityChanged.next({
      mediaId: result.mediaId,
      quality: result.newQuality,
    });
  }

  onVideoLogoAnalyzeFinished(result) {
    this.videoLogoAnalyzeFinished.next({
      uploadSuccess: result.UploadSuccess,
      url: result.Url,
    });
  }
}
